/**
 * Detects the text direction based on the content
 * Returns 'rtl' for Arabic/Hebrew text, 'ltr' for Latin-based text
 */
export const detectTextDirection = (text: string): 'rtl' | 'ltr' => {
  if (!text || text.trim().length === 0) return 'ltr';
  
  // Arabic Unicode ranges: 0600-06FF (Arabic), 0750-077F (Arabic Supplement), 
  // 08A0-08FF (Arabic Extended-A), FB50-FDFF (Arabic Presentation Forms-A),
  // FE70-FEFF (Arabic Presentation Forms-B)
  // Hebrew Unicode range: 0590-05FF
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  
  // Test if text contains any RTL characters
  const hasRtlChars = rtlRegex.test(text);
  
  if (hasRtlChars) {
    return 'rtl';
  }
  
  return 'ltr';
};

/**
 * Gets the appropriate text alignment based on direction
 */
export const getTextAlign = (text: string): 'right' | 'left' => {
  return detectTextDirection(text) === 'rtl' ? 'right' : 'left';
};

/**
 * Wraps text with proper Unicode bidirectional markers for correct punctuation display
 * RLM (Right-to-Left Mark): \u200F
 * LRM (Left-to-Right Mark): \u200E
 */
export const wrapTextWithBidiMarkers = (text: string): string => {
  if (!text) return text;
  
  const direction = detectTextDirection(text);
  
  if (direction === 'rtl') {
    // Add RLM after punctuation to keep it at the end of Arabic sentences
    return text.replace(/([.!?,;:])/g, '$1\u200F');
  }
  
  return text;
};
